import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  EMPRESAS_KEYS, listarEmpresas, criarEmpresa, type CreateCompanyPayload,
  atualizarEmpresa, deletarEmpresa, reativarEmpresa, type UpdateCompanyPayload,
  BANK_ACCOUNTS_KEYS, listarContasBancarias, criarContaBancaria,
  atualizarContaBancaria, deletarContaBancaria, type BankAccountPayload,
} from '../api';

interface ListParams {
  cnpj?: string;
  isActive?: boolean;
  limit?: number;
  offset?: number;
}

export function useEmpresas(params?: ListParams) {
  return useQuery({
    queryKey: EMPRESAS_KEYS.list(params),
    queryFn: () => listarEmpresas(params),
  });
}

export function useCriarEmpresa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCompanyPayload) => criarEmpresa(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EMPRESAS_KEYS.all }),
  });
}

export function useAtualizarEmpresa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCompanyPayload }) =>
      atualizarEmpresa(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EMPRESAS_KEYS.all }),
  });
}

export function useDeletarEmpresa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletarEmpresa(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EMPRESAS_KEYS.all }),
  });
}

export function useReativarEmpresa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reativarEmpresa(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EMPRESAS_KEYS.all }),
  });
}

export function useContasBancarias(companyId: string) {
  return useQuery({
    queryKey: BANK_ACCOUNTS_KEYS.all(companyId),
    queryFn: () => listarContasBancarias(companyId),
    enabled: !!companyId,
  });
}

export function useCriarContaBancaria(companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: BankAccountPayload) => criarContaBancaria(companyId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: BANK_ACCOUNTS_KEYS.all(companyId) }),
  });
}

export function useAtualizarContaBancaria(companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bankId, payload }: { bankId: string; payload: Partial<BankAccountPayload> }) =>
      atualizarContaBancaria(companyId, bankId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: BANK_ACCOUNTS_KEYS.all(companyId) }),
  });
}

export function useDeletarContaBancaria(companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bankId: string) => deletarContaBancaria(companyId, bankId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: BANK_ACCOUNTS_KEYS.all(companyId) }),
  });
}
